import { LinearProgress } from '@mui/joy';

import { useContext, useEffect, useRef, useState } from 'react';
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels';
import { z } from 'zod';

import { CollatedRunTree } from '@/Pages/Conversation/CollatedConversation';
import { THREAD_RUN_SELECT } from '@/Pages/Conversation/constants';
import { hasMoreInfiniteItems } from '@/Pages/Conversation/utils/hasMoreInfiniteItems';
import { useThreadChildRuns } from '@/Pages/Conversation/utils/useThreadChildRuns';
import { RunDetailsPanel } from '@/Pages/Run/components/RunDetailsPanel';
import { useDetectXSPanel } from '@/Pages/Run/utils/useDetectXSPanel';
import { useGroupChildByParentId } from '@/Pages/Run/utils/useGroupChildByParentId';
import { CHILD_RUNS_SELECT } from '@/Pages/Run/utils/utils';
import { cn } from '@/design-system/utils/cn';
import { useRun, useRunsInfinite } from '@/hooks/useSwr';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import { USE_RUN_OPTIONS } from '../../../constants';
import { StateContext } from '../../../control/state';
import { useActiveThreadId } from '../../../hooks/useActiveThreadId';
import { LangSmithAwareContext } from '../../../langsmith';
import { getNodeNameFromRun } from '../../../utils';

const REFRESH_INTERVAL = 250;
const PANEL_ID = 'trace-panel-studio';

export const TraceView = ({
  peekedRunId,
  setPeekedRunId,
}: {
  peekedRunId: string;
  setPeekedRunId: (runId: string | undefined) => void;
}) => {
  const { setHoverSelection, runState } = useContext(StateContext);
  const isRunning = runState.type === 'running';
  const [searchFilter, setSearchFilter] = useState('');

  const { threadId } = useActiveThreadId();

  useEffect(() => {
    if (!threadId) {
      setPeekedRunId(undefined);
    }
  }, [threadId, setPeekedRunId]);

  const { data: rootRun, isLoading: isRootRunLoading } = useRun({
    id: peekedRunId,
    excludeSerialized: true,
    withContents: true,
    options: {
      ...USE_RUN_OPTIONS(isRunning ? undefined : 3),
      // refresh the root run while we are running. There's also a case where the run completes but the outputs are not yet available.
      ...(isRunning
        ? { refreshInterval: REFRESH_INTERVAL }
        : {
            refreshInterval: (data) =>
              data?.outputs === null ? REFRESH_INTERVAL : 0,
          }),
    },
  });
  const { session_id: sessionId } = rootRun || {};

  const rootRunsQuery = useRunsInfinite(
    {
      session: [sessionId || ''],
      is_root: true,
      filter: `eq(thread_id, "${threadId}")`,
      order: 'asc',
      select: THREAD_RUN_SELECT,
    },
    undefined,
    {
      ...(isRunning && { refreshInterval: REFRESH_INTERVAL }),
    }
  );

  const filterRunsQuery = useRunsInfinite(
    searchFilter
      ? {
          filter: `and(${searchFilter}, eq(thread_id, "${threadId}"))`,
          session: [sessionId || ''],
          select: CHILD_RUNS_SELECT,
          skip_prev_cursor: true,
          order: 'asc',
        }
      : null,

    undefined,
    {
      keepPreviousData: false,
      ...(isRunning && { refreshInterval: REFRESH_INTERVAL }),
    }
  );

  const { data: rootRunsPages, isLoading: isRootRunsLoading } = rootRunsQuery;

  const { data: filterRunPages, isLoading: isFilterRunsLoading } =
    filterRunsQuery;

  const rootRuns = rootRunsPages?.flatMap((page) => page);
  const filterRuns = filterRunPages?.flatMap((page) => page);

  const { data: threadChildRuns } = useThreadChildRuns({
    selectedRootRun: rootRun,
    options: {
      ...(isRunning && { refreshInterval: REFRESH_INTERVAL }),
    },
  });
  const threadFilteredChildRuns = useGroupChildByParentId(
    filterRuns,
    isFilterRunsLoading
  );

  const hasMoreFilteredRuns = hasMoreInfiniteItems(filterRunsQuery);
  const hasMoreRuns = hasMoreInfiniteItems(rootRunsQuery);

  const isNextPageValidating =
    (filterRunsQuery.isValidating &&
      filterRunsQuery.size > 1 &&
      filterRunsQuery.data?.length != filterRunsQuery.size) ||
    (rootRunsQuery.isValidating &&
      rootRunsQuery.size > 1 &&
      rootRunsQuery.data?.length != rootRunsQuery.size);

  const { selectedPlaygroundRun, setSelectedPlaygroundRun } =
    useContext(StateContext);
  const { PlaygroundPane } = useContext(LangSmithAwareContext);
  const panelRef = useRef<ImperativePanelHandle>(null);
  const runTreeRef = useRef<HTMLDivElement>(null);
  const toggleRunTreeSize = () => {
    const prevSize = panelRef.current?.getSize();
    if (prevSize) {
      panelRef.current?.resize(prevSize > 6 ? 6 : 40);
    }
  };
  const isXsPanel = useDetectXSPanel({
    panelId: PANEL_ID,
    panelRef: runTreeRef,
  });

  const [showGraphStats, setShowGraphStats] = useLocalStorageState(
    `ls:threadDetails:showGraphStats`,
    false,
    z.boolean()
  );

  const toggleGraphStats = (show: boolean) => {
    setShowGraphStats(show);
    setTimeout(() => {
      panelRef.current?.resize(show ? 50 : 40);
    }, 0);
  };
  return (
    <>
      <PanelGroup
        autoSaveId={`collated-conversation-studio`}
        direction="horizontal"
        className="mt-0.5 flex-grow border-t border-secondary"
      >
        <Panel
          ref={panelRef}
          minSize={6}
          defaultSize={40}
          className="relative flex min-w-[115px] flex-col"
        >
          <CollatedRunTree
            treeRef={runTreeRef}
            // show loading if loading first root run
            isRootRunsLoading={
              isRootRunsLoading || (isRootRunLoading && !rootRun)
            }
            isFilteredRunsLoading={isFilterRunsLoading}
            searchFilter={searchFilter}
            setSearchFilter={(filter) => setSearchFilter(filter || '')}
            filteredRuns={filterRuns}
            rootRuns={rootRuns || []}
            selectedRun={peekedRunId ? rootRun : undefined}
            threadChildRuns={
              searchFilter && threadFilteredChildRuns
                ? threadFilteredChildRuns
                : threadChildRuns
            }
            isXsPanel={isXsPanel}
            togglePanelSize={toggleRunTreeSize}
            onNavigate={(run) => {
              if (!run) return;
              setPeekedRunId(run.id);
              const nodeName = getNodeNameFromRun(run);
              setHoverSelection(nodeName || null);
            }}
            onMouseEnter={(r) => {
              const nodeName = getNodeNameFromRun(r);
              if (!nodeName) return;
              setHoverSelection(nodeName);
            }}
            onMouseLeave={() => {
              if (!rootRun) {
                setHoverSelection(null);
                return;
              }
              const nodeName = getNodeNameFromRun(rootRun);

              setHoverSelection(nodeName || null);
            }}
            hasMoreRuns={searchFilter ? hasMoreFilteredRuns : hasMoreRuns}
            isNextPageValidating={isNextPageValidating}
            getNextPage={() => {
              if (searchFilter) {
                filterRunsQuery.setSize((size) => size + 1);
              } else {
                rootRunsQuery.setSize((size) => size + 1);
              }
            }}
            panelId={PANEL_ID}
            showGraphStats={showGraphStats}
            toggleGraphStats={toggleGraphStats}
          />
        </Panel>
        <PanelResizeHandle className="group relative flex items-stretch text-secondary data-[resize-handle-active]:text-ls-blue">
          <div className="w-[1px] bg-tertiary" />
          <div className="absolute inset-y-0 -left-[4px] z-10 w-[9px] opacity-0" />
          <div className="absolute inset-y-0 w-[2px] bg-current opacity-0 transition-all group-hover:opacity-100" />
        </PanelResizeHandle>
        <Panel className="flex min-w-0 flex-col !overflow-visible">
          {isRootRunLoading && (
            <div>
              <LinearProgress />
            </div>
          )}
          <div
            className={cn(
              'mt-4 flex h-full flex-col',
              isRootRunLoading && 'mt-2.5'
            )}
          >
            <RunDetailsPanel
              run={peekedRunId ? rootRun : undefined}
              isRunLoading={isRootRunLoading}
              isPublicPage={false}
              sidebarVisible={false}
            />
          </div>
        </Panel>
      </PanelGroup>
      {PlaygroundPane && (
        <PlaygroundPane
          playgroundRun={selectedPlaygroundRun}
          nodeName={getNodeNameFromRun(selectedPlaygroundRun)}
          onClose={() => setSelectedPlaygroundRun(undefined)}
        />
      )}
    </>
  );
};
